<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-layout row wrap justify-space-between>
        <dt-search-box
          :search="search"
          @typing="searchValue"
          @reset-datatable="resetDataTable()"
          @clear-search-field="search = null"
        />
        <v-layout row wrap justify-end class="pt-2 pb-2">
          <v-btn dark color="primary" v-if="hasPermissions(['promo_cards_store'])" @click="dialogs.addDialog = true">
            <v-icon>add</v-icon>
            {{ $t('datatable-add-new') }}
          </v-btn>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card flat>
      <v-layout row wrap justify-end>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 0px; height: 30px; width: 30px"
              :loading="loadThePrintBtn"
              flat
              fab
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('print')"
            >
              <v-icon>print</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Print Table`) }}</span>
        </v-tooltip>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 20px 5px 10px; height: 30px; width: 30px"
              fab
              :loading="loadTheExportBtn"
              flat
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('export')"
            >
              <v-icon>fa-file-excel</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Export Table`) }}</span>
        </v-tooltip>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4">
      <v-data-table
        v-show="isDatatable"
        :headers="headers"
        :items="resultData.data"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="pagination.rowsPerPageItems"
        :total-items="resultData.total"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.promo_card_id }}</td>-->
          <td>{{ props.item.promo_card_number }}</td>
          <td>{{ props.item.customer_name }}</td>
          <td>
            <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
              <a
                slot="activator"
                @click="openPromoOutletNameDialog(props.item.promo_card_outlets, props.item.promo_card_id)"
              >
                <v-icon>subtitles</v-icon>
              </a>
              <span>{{ $t(`View Promo Card Outlet`) }}</span>
            </v-tooltip>
          </td>
          <td>{{ props.item.promo_card_discount }}%</td>
          <td>
            <v-tooltip top>
              <v-chip
                slot="activator"
                label
                :color="props.item.promo_card_status === 'Active' ? 'success' : 'error'"
                text-color="white"
                class="no-select"
              >
                <v-icon>{{ props.item.promo_card_status === 'Active' ? 'check_circle' : 'block' }}</v-icon>
              </v-chip>
              <span>{{ props.item.promo_card_status === 'Active' ? $t('Active') : $t('Inactive') }}</span>
            </v-tooltip>
          </td>
          <td v-if="hasActionsMenuPermissions()">
            <v-menu offset-y bottom :left="!$vuetify.rtl" :right="$vuetify.rtl" transition="slide-y-reverse-transition">
              <v-btn flat fab icon class="transparent py-2" slot="activator">
                <v-icon large class="grey--text actions-btn">more_vert</v-icon>
              </v-btn>
              <v-list>
                <v-tooltip v-if="hasPermissions(['promo_cards_show'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile
                    slot="activator"
                    @click="viewRecord(props.item.promo_card_outlets, props.item.promo_card_id)"
                  >
                    <v-icon>chrome_reader_mode</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-view') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`View Promo Card information`) }}</span>
                </v-tooltip>

                <v-tooltip v-if="hasPermissions(['promo_cards_update'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="editRecord(props.item.promo_card_id)">
                    <v-icon>edit</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-edit') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Edit Promo Card information`) }}</span>
                </v-tooltip>

                <v-divider v-if="hasPermissions(['promo_cards_destroy'])" />

                <v-tooltip v-if="hasPermissions(['promo_cards_destroy'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="showConfirmDeletion(props.item.promo_card_id)">
                    <v-icon class="red--text">delete</v-icon>
                    <v-list-tile-content class="red--text px-3">{{ $t('datatable-delete') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t('Delete Promo Card Record') }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>

      <dt-pagination
        :resultData="resultData"
        :pagination="pagination"
        :perPageNumber="perPageNumber"
        @page-changed="pageChanged"
        @per-page-changed="resultsPerPageChanged"
      />
    </v-card>

    <add-promo-card
      :addDialog="dialogs.addDialog"
      @reset-datatable="resetDataTable(), resetAllErrors(true)"
      @hidemodal="resetAllErrors(true), (dialogs.addDialog = false)"
      @add-customer="dialogs.addCustomer = true"
    />

    <edit-promo-card
      :editDialog="dialogs.editDialog"
      @updated="updateDatatableResult(), (dialogs.editDialog = false), (allowGettingData = false)"
      @hidemodal="resetAllErrors(true), (dialogs.editDialog = false)"
      @add-customer="dialogs.addCustomer = true"
    />

    <confirm-deletion
      :deleteDialog="dialogs.deleteDialog"
      :btnDeleteLoading="btnDeleteLoading"
      @hidemodal="dialogs.deleteDialog = false"
      @confirm="deleteAction()"
    />

    <view-promo-card
      :viewDialog="dialogs.viewDialog"
      @hidemodal="dialogs.viewDialog = false"
      :promoOutlets="outeltName"
    />

    <view-promo-outlets
      :viewPromoOutletDialog="dialogs.viewPromoOutletDialog"
      @hidemodal="dialogs.viewPromoOutletDialog = false"
      :promoOutlets="outeltName"
      :promoCardId="promoCardID"
    />

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />

    <add-customer
      :addDialog="dialogs.addCustomer"
      @reset-datatable="setCustomersList()"
      @hidemodal="resetAllErrors(true), (dialogs.addCustomer = false)"
      @add-group="dialogs.addCustomerGroup = true"
      @add-salesman="dialogs.addSalesman = true"
    />

    <add-salesman
      :addDialog="dialogs.addSalesman"
      @reset-datatable="updateFullSaleManList()"
      @hidemodal="resetAllErrors(true), (dialogs.addSalesman = false)"
      @add-staff="dialogs.addStaff = true"
    />

    <add-customer-group
      :addDialog="dialogs.addCustomerGroup"
      @reset-datatable="updateFullGroupsList()"
      @hidemodal="resetAllErrors(true), (dialogs.addCustomerGroup = false)"
    />

    <add-staff
      :addDialog="dialogs.addStaff"
      @reset-datatable="setStaffsList()"
      @hidemodal="resetAllErrors(true), (dialogs.addStaff = false)"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    dtPagination: () => import('@/views/_partials/AppDatatablePagination.vue'),
    addPromoCard: () => import('./addPromoCard.vue'),
    editPromoCard: () => import('./editPromoCard.vue'),
    viewPromoCard: () => import('./viewPromoCard.vue'),
    viewPromoOutlets: () => import('./viewPromoOutlets.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue'),
    addCustomer: () => import('@/views/customers/customers/addCustomer.vue'),
    addSalesman: () => import('@/views/staff/salesmen/addSalesman.vue'),
    addCustomerGroup: () => import('@/views/customers/groups/addGroup.vue'),
    addStaff: () => import('@/views/staff/staff/addStaff.vue')
  },

  data() {
    return {
      datatableTitle: this.$t('Promo Cards'),
      dialogs: {
        viewDialog: false,
        addDialog: false,
        editDialog: false,
        deleteDialog: false,
        viewPromoOutletDialog: false,
        printExportDialog: false,
        addCustomer: false,
        addSalesman: false,
        addCustomerGroup: false,
        addStaff: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      deleteId: 0,
      btnDeleteLoading: false,
      isDatatable: true,
      search: null,
      oldSearch: null,
      loading: false,
      outeltName: [],
      promoCardID: '',
      defaultSortBy: 'promo_card_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'promo_card_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Promo Card Number'), value: 'promo_card_number' },
        { text: this.$t('Customer Name'), value: 'customer_name' },
        { text: this.$t('Outlet'), value: 'promo_card_outlets' },
        { text: this.$t('Discount'), value: 'promo_card_discount' },
        { text: this.$t('Status'), value: 'promo_card_status' }
      ],
      allowGettingData: true
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: this.$t('Promo Cards'),
      icon: 'attach_money'
    })

    if (this.hasActionsMenuPermissions()) {
      this.headers.push({
        text: this.$t('datatable-actions'),
        value: '',
        sortable: false
      })
    }

    this.setOutletsList()
    this.setCustomersList()
    this.updateFullGroupsList()
    this.updateFullSaleManList()
    this.setStaffsList()
  },

  computed: {
    ...mapGetters({
      resultData: 'promoCards/getPromoCardList',
      outletsList: 'outlets/getFullOutletsList'
    })
  },

  watch: {
    pagination: {
      immediate: true,
      deep: true,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'promoCards/updatePromoCardList',
      deleteRecord: 'promoCards/deletePromoCard',
      fetchPromoCardInfo: 'promoCards/fetchPromoCardInfo',
      setCustomersList: 'customers/setFullCustomersList',
      setOutletsList: 'outlets/setFullOutletsList',
      updateFullSaleManList: 'salesmen/updateFullSalesmanList',
      updateFullGroupsList: 'customersGroups/updateFullGroupsList',
      setStaffsList: 'staff/setFullStaffsList'
    }),

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get('promo_cards_index', {
        params: { is_paginated: 0, search: this.search }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    openPromoOutletNameDialog(promoCardOutlet, promoCardID) {
      this.dialogs.viewPromoOutletDialog = true
      this.getPromoOutletName(promoCardOutlet, promoCardID)
    },

    getPromoOutletName(promoCardOutlet, promoCardID) {
      this.promoCardID = String(promoCardID)
      this.outeltName = []
      promoCardOutlet = promoCardOutlet.split(',').map(o => {
        return parseInt(o)
      })
      this.outeltName = this.outletsList
        .map(outlet => {
          let exist = false
          promoCardOutlet.forEach(f => {
            if (Object.values(outlet).indexOf(f) >= 0) {
              exist = true
            }
          })
          if (exist) {
            return outlet.text
          }
        })
        .filter(outelet => {
          return outelet != null
        })
    },

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    hasActionsMenuPermissions() {
      return helpers.userHasPermissions(['promo_cards_show', 'promo_cards_update', 'promo_cards_destroy'])
    },

    updateDatatableResult() {
      this.loading = true
      let payload = { ...this.pagination, search: this.search }
      this.updateResult(payload)
        .then(() => {
          this.pagination.page = this.resultData.current_page
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetDataTable() {
      this.allowGettingData = true
      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        totalItems: 1
      }
      this.search = this.oldSearch = null
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
    },

    viewRecord(promoCardOutlet, id) {
      this.getPromoOutletName(promoCardOutlet, id)
      this.fetchPromoCardInfo(id).then(() => {
        this.dialogs.viewDialog = true
      })
    },

    editRecord(id) {
      this.fetchPromoCardInfo(id).then(() => {
        this.dialogs.editDialog = true
      })
    },

    showConfirmDeletion(id) {
      this.deleteId = id
      this.dialogs.deleteDialog = true
    },

    deleteAction() {
      this.btnDeleteLoading = true
      this.deleteRecord(this.deleteId)
        .then(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
          let payload = { ...this.pagination, search: this.search }
          this.updateResult(payload)
        })
        .catch(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
        })
    }
  }
}
</script>

<style scoped />
